import { initTdx } from '../../../init/sdkTdx';
import { tdx_request, tdx_response } from "../../../tdx_proto/bundle";
import { getConnectionPool } from '../../../core/connectionPool';
import { hashString } from '../../../utils/hash';

interface OperationCountOverTime {
    CountOfOperations: {
        operation: string;
        count: number;
        denied: number;
    }[];
    operationsOverTime: {
        date: string[];
        reads: number[];
        writes: number[];
        deletes: number[];
        shares: number[];
        denied: number[];
    };
    privilegedDaily: {
        date: string;
        [key: string]: any;
    }[];
}

const PRIVILEGED_OP_TYPES = [
    "AddRole", "UpdateRole", "SetRolesToUser", "UpdateShare", "BlockUser", "RemoveOrganizationUser",
];

export async function OperationCountOverTime(
    startDate: number,
    endDate: number
): Promise<OperationCountOverTime> {

    const pool = await getConnectionPool();
    const pin = await hashString(pool.getPin());
    const data = pool.getToken();
    const client = await initTdx();
    let times = 10;
    while (client.is_open() == false && times >= 0) {
        console.log('🔄 Waiting for WebSocket to open...');
        times -= 1;
        await new Promise(resolve => setTimeout(resolve, 1000));

    }
    if (times < 0) {
        throw new Error("Connection Failed. Please try again.");
    }

    const req = tdx_request.Request.create({
        operationCountOverTime: tdx_request.OperationCountOverTime.create({
            pin,
            data,
            startDate,
            endDate,
        })
    });

    try {
        const res = await client.sendAndWait(req);

        client.close();
        if (res?.operationCountOverTime?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get operations over time ${res?.operationCountOverTime?.status}`);
        }
        if (!res?.operationCountOverTime?.operationCount) {
            throw Error('No operations found');
        }

        // ✅ 1. Top Operations aggregation
        const topOperations = res.operationCountOverTime.operationCount.map((item) => ({
            operation: item.operation,
            count: Number(item.count.reduce((acc: number, curr) => acc + Number(curr), 0)),
            denied: Number(item.deniedCount.reduce((acc: number, curr) => acc + Number(curr), 0)),
        }));

        // ✅ 2. Decide granularity
        const ONE_DAY = 24 * 3600 * 1000;
        const isHourly = (endDate - startDate) <= ONE_DAY;
        const step = isHourly ? 3600 * 1000 : ONE_DAY;

        // ✅ 3. Normalize function
        function normalize(ts: number) {
            const d = new Date(ts);
            if (isHourly) {
                d.setMinutes(0, 0, 0);
            } else {
                d.setHours(0, 0, 0, 0);
            }
            return d.getTime();
        }

        const startNorm = normalize(startDate);

        // ✅ 4. Build timeline
        const timeline: number[] = [];
        for (let t = startNorm; t <= endDate; t += step) {
            timeline.push(t);
        }

        // ✅ 5. Initialize output
        const operationsOverTime = {
            date: timeline.map(t => new Date(t).toISOString()),
            reads: new Array(timeline.length).fill(0),
            writes: new Array(timeline.length).fill(0),
            deletes: new Array(timeline.length).fill(0),
            shares: new Array(timeline.length).fill(0),
            denied: new Array(timeline.length).fill(0),
        };

        const privilegedDaily: { date: string, [key: string]: any }[] = timeline.map(t => {
            const obj: any = { date: new Date(t).toISOString().split('T')[0] };
            PRIVILEGED_OP_TYPES.forEach(op => obj[op] = 0);
            return obj;
        });

        // ✅ 6. Operation → category mapping
        function mapOperation(op: string): keyof typeof operationsOverTime {
            const lower = op.toLowerCase();
            if (lower.includes("get") || lower.includes("read")) return "reads";
            if (lower.includes("delete")) return "deletes";
            if (lower.includes("share") || lower.includes("invite")) return "shares";
            return "writes";
        }

        // ✅ 7. Fill time series
        res.operationCountOverTime.operationCount.forEach((item) => {
            const category = mapOperation(item.operation);
            const isPrivileged = PRIVILEGED_OP_TYPES.includes(item.operation);

            item.date.forEach((epochSec: number, idx: number) => {
                const ts = normalize(epochSec * 1000); // seconds → ms
                const index = Math.floor((ts - startNorm) / step);

                if (index >= 0 && index < timeline.length) {
                    operationsOverTime[category][index] += Number(item.count[idx]);
                    operationsOverTime.denied[index] += Number(item.deniedCount[idx]);

                    if (isPrivileged) {
                        privilegedDaily[index][item.operation] += Number(item.count[idx]);
                    }
                }
            });
        });
        console.log("operationsOverTime", operationsOverTime);
        console.log("privilegedDaily", privilegedDaily);
        console.log("topOperations", topOperations);

        return {
            CountOfOperations: topOperations,
            operationsOverTime,
            privilegedDaily
        };

    } catch (err) {
        client.close();
        throw Error('Error in OperationCountOverTime request: ' + err);
    }
}

export async function StorageDistribution(startDate: number, endDate: number): Promise<{
    key: string;
    gb: number;
    count: number;
}[]> {
    const pool = await getConnectionPool();
    const pin = await hashString(pool.getPin());
    const data = pool.getToken();
    const client = await initTdx();
    let times = 10;
    while (client.is_open() == false && times >= 0) {
        console.log('🔄 Waiting for WebSocket to open...');
        times -= 1;
        await new Promise(resolve => setTimeout(resolve, 1000));

    }
    if (times < 0) {
        throw new Error("Connection Failed. Please try again.");
    }
    const req = tdx_request.Request.create({
        storageDistribution: tdx_request.StorageDistribution.create({
            pin: pin,
            data: data,
            fromTime: startDate,
            toTime: endDate
        })
    });
    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.storageDistribution?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get storage distribution ${res?.storageDistribution?.status}`);
        }
        if (res?.storageDistribution?.storageItem) {
            const getCategory = (mimeType: string): string => {
                if (!mimeType) return "Others";

                const type = mimeType.toLowerCase().split("/")[0];

                switch (type) {
                    case "image":
                        return "Images";
                    case "video":
                        return "Videos";
                    case "audio":
                        return "Audio";
                    case "text":
                    case "message":
                        return "Documents";
                    case "application":
                        return "Applications";
                    case "font":
                        return "Fonts";
                    case "model":
                        return "3D Models";
                    case "multipart":
                        return "Multipart";
                    case "example":
                        return "Examples";
                    default:
                        return "Others";
                }
            };

            const grouped = res.storageDistribution.storageItem.reduce((acc, item) => {
                const category = getCategory(item.type || "");
                if (!acc[category]) {
                    acc[category] = { key: category, gb: 0, count: 0 };
                }
                acc[category].gb += Number(item.size || 0) / (1024 * 1024 * 1024);
                acc[category].count += Number(item.count || 0);
                return acc;
            }, {} as Record<string, { key: string, gb: number, count: number }>);

            const buckets = Object.values(grouped);
            console.log("buckets", buckets);
            return buckets;
        }
        throw Error('Error in request: No storage items found');
    } catch (err) {
        client.close();
        console.log("Error in StorageDistribution request:", err);
        throw Error('Error in StorageDistribution request:' + err);
    }
}

export async function TopMovers(
    startDate: number,
    endDate: number
): Promise<{
    downloads: { key: string; label: string; value: number }[];
    uploads: { key: string; label: string; value: number }[];
    shares: { key: string; label: string; value: number }[];
    activity: { key: string; label: string; value: number }[];
}> {
    const pool = await getConnectionPool();
    const pin = await hashString(pool.getPin());
    const data = pool.getToken();
    const client = await initTdx();
    let times = 10;
    while (client.is_open() == false && times >= 0) {
        console.log('🔄 Waiting for WebSocket to open...');
        times -= 1;
        await new Promise(resolve => setTimeout(resolve, 1000));

    }
    if (times < 0) {
        throw new Error("Connection Failed. Please try again.");
    }

    const req = tdx_request.Request.create({
        topMovers: tdx_request.TopMovers.create({
            pin,
            data,
            startDate,
            endDate,
        })
    });

    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.topMovers?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get top movers ${res?.topMovers?.status}`);
        }
        const result = {
            downloads: [] as { key: string; label: string; value: number }[],
            uploads: [] as { key: string; label: string; value: number }[],
            shares: [] as { key: string; label: string; value: number }[],
            activity: [] as { key: string; label: string; value: number }[],
        };

        if (res?.topMovers?.moverItems) {
            res.topMovers.moverItems.forEach((item) => {
                const op = (item.operation || "").toLowerCase();
                const mapped = (item.user || []).map((user, idx) => ({
                    key: user,
                    label: user,
                    value: Number(item.count?.[idx] || 0),
                }));

                if (op.includes("download")) result.downloads = mapped;
                else if (op.includes("upload")) result.uploads = mapped;
                else if (op.includes("share")) result.shares = mapped;
                else if (op.includes("all")) result.activity = mapped;
            });
        }

        return result;
    } catch (err) {
        client.close();
        throw Error('Error in TopMovers request: ' + err);
    }
}



export async function AccessByLocation(
    startDate: number,
    endDate: number
): Promise<{
    label: string;
    country: string;
    events: number;
    unique_users: number;
    tier: "trusted" | "allowed" | "blocked";
    allowed: boolean;
}[]> {
    const pool = await getConnectionPool();
    const pin = await hashString(pool.getPin());
    const data = pool.getToken();
    const client = await initTdx();
    let times = 10;
    while (client.is_open() == false && times >= 0) {
        console.log('🔄 Waiting for WebSocket to open...');
        times -= 1;
        await new Promise(resolve => setTimeout(resolve, 1000));

    }
    if (times < 0) {
        throw new Error("Connection Failed. Please try again.");
    }

    const req = tdx_request.Request.create({
        accessByLocation: tdx_request.AccessByLocation.create({
            pin,
            data,
            fromTime: startDate,
            toTime: endDate
        })
    });
    console.log("Access by location")

    try {
        const res = await client.sendAndWait(req);
        client.close();
        if (res?.accessByLocation?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get access by location ${res?.accessByLocation?.status}`);
        }
        const result = (res?.accessByLocation?.items || []).map(item => ({
            label: item.location || "Unknown",
            country: item.country || "Unknown",
            events: Number(item.eventsCount || 0),
            unique_users: Number(item.usersCount || 0),
            tier: (item.locationStatus === tdx_response.AccessByLocation.Item.LocationStatus.TRUSTED ? "trusted" :
                item.locationStatus === tdx_response.AccessByLocation.Item.LocationStatus.ALLOWED ? "allowed" : "blocked") as "trusted" | "allowed" | "blocked",
            allowed: item.locationStatus !== tdx_response.AccessByLocation.Item.LocationStatus.UNTRUSTED
        }));
        console.log("AccessByLocation", result);

        return result;
    } catch (err) {
        client.close();
        throw Error('Error in AccessByLocation request: ' + err);
    }
}

export async function ExternalShareTopUsers(
    startDate: number,
    endDate: number
): Promise<{
    key: string,
    label: string,
    value: number,
    adds: number,
    updates: number,
    removals: number,
}[]> {
    const pool = await getConnectionPool();
    const pin = await hashString(pool.getPin());
    const data = pool.getToken();
    const client = await initTdx();
    let times = 10;
    while (client.is_open() == false && times >= 0) {
        console.log('🔄 Waiting for WebSocket to open...');
        times -= 1;
        await new Promise(resolve => setTimeout(resolve, 1000));

    }
    if (times < 0) {
        throw new Error("Connection Failed. Please try again.");
    }

    const req = tdx_request.Request.create({
        externalShareTopUsers: tdx_request.ExternalShareTopUsers.create({
            pin,
            data,
            fromTime: startDate,
            toTime: endDate,
        })
    });
    console.log("request for external shares", req)
    try {
        const res = await client.sendAndWait(req);
        console.log(res);
        client.close();
        if (res?.externalShareTopUsers?.status != tdx_response.Status.SUCCESS) {
            throw Error(`failed to get external share top users ${res?.externalShareTopUsers?.status}`);
        }
        const result: {
            key: string;
            label: string;
            value: number;
            adds: number;
            updates: number;
            removals: number;
        }[] = [];

        (res?.externalShareTopUsers?.items || []).forEach(item => {
            const email = item.email || "Unknown";
            const adds = Number(item.addObject || 0) + Number(item.addSpace || 0) + Number(item.addUser || 0);
            const updates = Number(item.updatePermissions || 0);
            const removals = Number(item.removeObject || 0) + Number(item.removeSpace || 0) + Number(item.removeUser || 0);
            const total = adds + updates + removals;

            if (total > 0) {
                result.push({
                    key: email,
                    label: email,
                    value: total,
                    adds,
                    updates,
                    removals
                });
            }
        });
        console.log("ExternalShareTopUsers", result);

        return result;
    } catch (err) {
        client.close();
        throw Error('Error in ExternalShareTopUsers request: ' + err);
    }
}

export async function OffHoursRatio(
    startDate: number,
    endDate: number
): Promise<{ offHours: number }> {
    const pool = await getConnectionPool();
    const pin = await hashString(pool.getPin());
    const data = pool.getToken();
    const client = await initTdx();
    let times = 10;
    while (client.is_open() == false && times >= 0) {
        console.log('🔄 Waiting for WebSocket to open...');
        times -= 1;
        await new Promise(resolve => setTimeout(resolve, 1000));
    }
    if (times < 0) {
        throw new Error("Connection Failed. Please try again.");
    }

    const req = tdx_request.Request.create({
        overviewOffHoursRatio: tdx_request.Overview_OffHoursRatio.create({
            pin,
            data,
            fromTime: startDate,
            toTime: endDate,
        })
    });

    try {
        const res = await client.sendAndWait(req);
        client.close();

        const offHours = Number(res?.overviewOffHoursRatio?.offHours || 0);
        console.log("OffHoursRatio", offHours);

        return { offHours };
    } catch (err) {
        client.close();
        throw Error('Error in OffHoursRatio request: ' + err);
    }
}

export async function CollaborationFlow(
    startDate: number,
    endDate: number
): Promise<{
    actor: string;
    space: string;
    outcome: string;
    count: number;
}[]> {
    const pool = await getConnectionPool();
    const pin = await hashString(pool.getPin());
    const data = pool.getToken();
    const client = await initTdx();
    let times = 10;
    while (client.is_open() == false && times >= 0) {
        console.log('🔄 Waiting for WebSocket to open...');
        times -= 1;
        await new Promise(resolve => setTimeout(resolve, 1000));
    }
    if (times < 0) {
        throw new Error("Connection Failed. Please try again.");
    }

    const req = tdx_request.Request.create({
        collaborationFlow: tdx_request.CollaborationFlow.create({
            pin,
            data,
            fromTime: startDate,
            toTime: endDate,
        })
    });

    try {
        const res = await client.sendAndWait(req);
        client.close();

        const result = (res?.collaborationFlow?.items || []).map(item => ({
            actor: item.actor || "Unknown",
            space: item.space || "Unknown",
            outcome: item.outcome || "Other",
            count: Number(item.count || 0),
        }));

        console.log("CollaborationFlow", result);
        return result;
    } catch (err) {
        client.close();
        throw Error('Error in CollaborationFlow request: ' + err);
    }
}
